import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import {
  hasAllPermissions,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
} from "@/shared";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from "@/shared/components/ui";
import { IDataMotivo } from "@/shared/interfaces/erp/inventario/motivo.interface";
import { useUiConfirmModalStore } from "@/store/ui";
import { useFetchMotivos, useUpdateMotivo } from "@/shared/actions/erp";
import { PermissionsEnum } from "@/auth/interfaces";

const returnUrlMotivo = "/parametrizacion/motivos";

export interface MotivoPageInterface {}

const MotivosPage: React.FC<MotivoPageInterface> = () => {
  useCheckPermissions(PermissionsEnum.api_view_motivo);

  const navigate = useNavigate();

  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );

  ///* table
  const { globalFilter, pagination, onChangeFilter, setPagination } =
    useTableFilter();
  const { pageIndex, pageSize } = pagination;

  const upateMotivo = useUpdateMotivo({
    navigate,
    returnUrl: returnUrlMotivo,
  });
  ///* handlers
  const onEdit = (original: IDataMotivo) => {
    navigate(`${returnUrlMotivo}/editar/${original.id_motivo}`);
  };
  const {
    data: MotivosPagingRes,
    isLoading,
    isRefetching,
  } = useFetchMotivos({
    page: pageIndex + 1,
    page_size: pageSize,
  });
  ///* columns
  const columns = useMemo<MRT_ColumnDef<IDataMotivo>[]>(
    () => [
      {
        accessorKey: "numero_referencia",
        header: "Numero referencia",
        size: 90,
      },
      {
        accessorKey: "nombre",
        header: "Nombre",
        size: 300,
      },
      {
        accessorKey: "modulo",
        header: "Modulo",
        size: 300,
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SwitchComponente
            estadoSwitch={row.original.estado}
            titulo={row.original.estado ? "Activo" : "Inactivo"}
            setEstadoSwitch={(motivo) => {
              if (!hasPermission(PermissionsEnum.api_change_motivo)) return;
              setConfirmDialog({
                isOpen: true,
                title: "Cambiar Estado",
                subtitle:
                  "¿Está seguro que desea cambiar el estado de este registro ?",
                onConfirm: () => {
                  setConfirmDialogIsOpen(false);
                  upateMotivo.mutate({
                    id: row.original.id_motivo,
                    data: {
                      ...row.original,
                      estado: motivo as boolean,
                    },
                  });
                },
              });
            }}
          />
        ),
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="Motivo"
      urlRedirect={`${returnUrlMotivo}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_motivo)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="Motivo"
      />

      <GeneralTable<IDataMotivo>
        columns={columns}
        data={MotivosPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={MotivosPagingRes?.data?.count}
        enableColumnActions={hasAllPermissions([
          PermissionsEnum.api_change_motivo,
        ])}
        // // actions
        canEdit={hasAllPermissions([PermissionsEnum.api_change_motivo])}
        // crud
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default MotivosPage;
